import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { isNil } from "lodash";

// Layouts
import Button from "@/Layouts/components/Buttons/Button";

// Components
import MetricTierItem from "./MetricTierItem";

// MUI
import { Grid, Paper } from "@mui/material";

// Icons
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import RemoveCircleRoundedIcon from "@mui/icons-material/RemoveCircleRounded";

const defaultInputMetric = () => {
	return {
		value: 0,
		input: "Percent",
	};
};

const metricItem = () => {
	return {
		missed: defaultInputMetric(),
		attained: defaultInputMetric(),
		numberRequired: 0,
	};
};

const hasTiersErrors = (errors = []) => errors.some((error) => error.missed || error.attained || error.numberRequired);

const isDefinitionValid = (tier, definition = { missed: "missed", attained: "attained" }) => {
	const { missed, attained } = definition;
	const missedValue = tier[missed]?.value;
	const attainedValue = tier[attained]?.value;

	if (isNil(missedValue) || isNil(attainedValue)) {
		return false;
	}

	return missedValue <= attainedValue;
};

const getCompareErrors = (tiers = [], index = 0, definition = { missed: "missed", attained: "attained" }) => {
	const { missed, attained } = definition || {};
	const current = tiers[index];
	const next = tiers?.[index + 1];

	if (!current || !next) {
		return { [missed]: false, [attained]: false, numberRequired: false };
	}

	const { [missed]: currentMissed, [attained]: currentAttained, numberRequired } = current || {};
	const { [missed]: nextMissed, [attained]: nextAttained, numberRequired: nextRequired } = next || {};

	return {
		[missed]: nextMissed.value < currentMissed.value,
		[attained]: nextAttained.value < currentAttained.value,
		numberRequired: Number(nextRequired) <= Number(numberRequired),
	};
};

const getTiersErrors = (tiers = [], metrics = [], definition = { missed: "missed", attained: "attained" }) => {
	const { missed, attained } = definition || {};

	if (!tiers?.length || !metrics?.length) {
		return [];
	}

	const errors = Array.from({ length: tiers.length }, () => ({
		[missed]: false,
		[attained]: false,
		numberRequired: false,
	}));

	for (let i = 0; i < tiers.length; i++) {
		const tier = tiers[i];		

		if (!isDefinitionValid(tier, { missed, attained })) {
			errors[i] = { [missed]: true, [attained]: true, numberRequired: true };
			continue;
		}

		const hasNextTier = tiers?.[i + 1];
		if (hasNextTier) {
			errors[i + 1] = getCompareErrors(tiers, i, { missed, attained });
		}
	}

	return errors;
};

const getOppositeIdentifier = (identifier, { aboveIdentifier = "attained", belowIdentifier = "missed" }) => {
	const opposite = identifier === aboveIdentifier ? belowIdentifier : aboveIdentifier;
	return {
		opposite,
		isBelowIdentifier: opposite === belowIdentifier,
	};
};

const getUpdatedTierByInput = (tier, areas = [], input = "Percent") => {
	if (!tier || !areas?.length) {
		return tier;
	}
	return areas.reduce((acc, area) => {
		acc[area]["input"] = input;
		acc[area]["value"] = 0;
		return acc;
	}, tier);
};
export default function RequiredReferences(props) {
	const {
		aboveIdentifier = "attained",
		belowIdentifier = "missed",
		metrics = [],
		tiers = [],
		labels = {
			required: "Required",
			above: "Above",
			below: "Below",
		},
		loading = false,
		readOnly = false,
		handleChangeTiers = () => {},
		onActionValidation = () => {},
	} = props;

	const [innerTiers, setInnerTiers] = useState([]);

	const errors = getTiersErrors(innerTiers, metrics, { missed: belowIdentifier, attained: aboveIdentifier });

	const updateTiersInputs = (updatedTiers = [], input = "Percent") => {
		return updatedTiers?.map((tier) => {
			if (tier?.[belowIdentifier]) {
				tier[belowIdentifier]["input"] = input;
			}
			if (tier?.[aboveIdentifier]) {
				tier[aboveIdentifier]["input"] = input;
			}
			return tier;
		});
	};

	const onHandleAddTier = () => {
		const prevIndex = innerTiers.length - 1;

		let updated = [...innerTiers, metricItem()];
		const input = updated[0]?.[belowIdentifier]?.input || "Percent";
		updated = updateTiersInputs(updated, input);
		const prevAttainedValue = updated?.[prevIndex]?.[aboveIdentifier]?.["value"] || 0;
		updated[updated.length - 1][belowIdentifier]["value"] = prevAttainedValue;

		setInnerTiers(updated);

		// Validating if the tiers have errors
		const isValid = !hasTiersErrors(getTiersErrors(updated, metrics, { missed: belowIdentifier, attained: aboveIdentifier }));
		onActionValidation?.(isValid);
		handleChangeTiers(updated, "adding");
	};

	const onHandleRemoveTier = () => {
		const updated = [...innerTiers];
		if (updated.length > 1) {
			updated.splice(-1);
		}
		setInnerTiers(updated);

		// Validating if the tiers have errors
		const isValid = !hasTiersErrors(getTiersErrors(updated, metrics, { missed: belowIdentifier, attained: aboveIdentifier }));
		onActionValidation?.(isValid);
		handleChangeTiers(updated);
	};

	const onHandleUpdateItem = (index, data, updateInformation = {}) => {
		let updated = [...innerTiers];
		updated[index] = data;

		const { area, isUpdatingInput } = updateInformation;
		if (isUpdatingInput) {
			const inputValue = data[area]["input"];
			updated = updated.map((tier) => getUpdatedTierByInput(tier, [aboveIdentifier, belowIdentifier], inputValue));
		} else if (area) {
			const { opposite, isBelowIdentifier } = getOppositeIdentifier(area, { aboveIdentifier, belowIdentifier });
			const propagateValue = data[area]["value"];
			if (updated?.[index + 1] && isBelowIdentifier) {
				updated[index + 1][opposite]["value"] = propagateValue;
			}
		}

		setInnerTiers(updated);

		const isValid = !hasTiersErrors(getTiersErrors(updated, metrics, { missed: belowIdentifier, attained: aboveIdentifier }));
		onActionValidation?.(isValid);
		handleChangeTiers(updated, isUpdatingInput ? "input" : "update");
	};

	useEffect(() => {
		setInnerTiers(tiers);
		return () => {
			setInnerTiers([]);
		};
	}, [tiers]);

	const isMaxLimitTiers = () => {
		const totalMetrics = metrics?.length || 0;
		const totalTiers = innerTiers?.length || 0;
		return Boolean(totalTiers) && totalTiers >= totalMetrics;
	};

	return (
		<Grid container justifyContent={"center"} mt={2} columnSpacing={2} sx={{ maxWidth: "465px !important" }}>
			{innerTiers.map((item, key) => {
				const itemKey = `required_reference_metric_${key}`;
				return (
					<MetricTierItem
						key={itemKey}
						index={key}
						metrics={metrics}
						metricItem={item}
						labels={labels}
						errors={errors?.[key]}
						aboveIdentifier={aboveIdentifier}
						belowIdentifier={belowIdentifier}
						showTitles={key === 0}
						loading={loading}
						readOnly={readOnly}
						handleUpdateMetric={(updated, updateInformation) => onHandleUpdateItem(key, updated, updateInformation)}
					/>
				);
			})}

			<Grid container item mt={2.5} columnSpacing={2} justifyContent="center" alignContent="center">
				<Grid item>
					<Paper elevation={0}>
						<Button
							variant="contained"
							text="Remove Tier"
							classes={"tier-btn remove-tier-btn"}
							bgColor={"var(--gray-blue-color)"}
							endIcon={<RemoveCircleRoundedIcon />}
							disabled={readOnly || innerTiers?.length <= 1}
							onClick={() => onHandleRemoveTier()}
						/>
					</Paper>
				</Grid>
				<Grid item>
					<Paper elevation={0}>
						<Button
							variant="contained"
							text="Add Tier"
							classes={"tier-btn add-tier-btn"}
							bgColor={"var(--primary-color)"}
							endIcon={<AddCircleRounded />}
							disabled={readOnly || isMaxLimitTiers()}
							onClick={() => onHandleAddTier()}
						/>
					</Paper>
				</Grid>
			</Grid>
		</Grid>
	);
}
RequiredReferences.propTypes = {
	aboveIdentifier: PropTypes.string,
	belowIdentifier: PropTypes.string,
	metrics: PropTypes.array,
	tiers: PropTypes.array,
	labels: PropTypes.shape({
		required: PropTypes.string,
		above: PropTypes.string,
		below: PropTypes.string,
	}),
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	handleChangeTiers: PropTypes.func,
	onActionValidation: PropTypes.func,
};
